import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResultsCard = ({ results, onDownloadReport, onBookConsultation }) => {
  const getMaturityLevel = (score) => {
    if (score >= 4.5) return { level: 'Advanced', color: 'text-green-600', bg: 'bg-green-50' };
    if (score >= 3.5) return { level: 'Proficient', color: 'text-blue-600', bg: 'bg-blue-50' };
    if (score >= 2.5) return { level: 'Developing', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (score >= 1.5) return { level: 'Minimal', color: 'text-orange-600', bg: 'bg-orange-50' };
    return { level: 'Basic', color: 'text-red-600', bg: 'bg-red-50' };
  };

  const overallScore = Object.values(results?.sectionScores)?.reduce((sum, score) => sum + score, 0) / Object.keys(results?.sectionScores)?.length;
  const maturity = getMaturityLevel(overallScore);

  const sectionDetails = [
    { key: 'dataInfrastructure', name: 'Data Infrastructure', icon: 'Database' },
    { key: 'analyticsCapabilities', name: 'Analytics Capabilities', icon: 'BarChart3' },
    { key: 'governanceMaturity', name: 'Governance Maturity', icon: 'Shield' },
    { key: 'teamSkills', name: 'Team Skills', icon: 'Users' },
    { key: 'strategicAlignment', name: 'Strategic Alignment', icon: 'Target' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-elevation p-4 sm:p-6 lg:p-8">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-brand rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Award" size={28} color="white" className="sm:w-8 sm:h-8" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2">Your Data Maturity Score</h2>
        <div className={`inline-flex items-center px-3 sm:px-4 py-2 rounded-full ${maturity?.bg} ${maturity?.color} font-semibold text-base sm:text-lg`}>
          {overallScore?.toFixed(1)}/5.0 - {maturity?.level}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {sectionDetails?.map((section) => {
          const score = results?.sectionScores?.[section?.key] || 0;
          const sectionMaturity = getMaturityLevel(score);
          
          return (
            <div key={section?.key} className="text-center p-3 sm:p-4 bg-surface rounded-lg hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer">
              <Icon name={section?.icon} size={20} className="mx-auto mb-2 text-secondary sm:w-6 sm:h-6" />
              <h4 className="font-semibold text-xs sm:text-sm text-primary mb-1 line-clamp-2">{section?.name}</h4>
              <div className={`text-base sm:text-lg font-bold ${sectionMaturity?.color}`}>
                {score?.toFixed(1)}
              </div>
              <div className={`text-xs ${sectionMaturity?.color}`}>
                {sectionMaturity?.level}
              </div>
            </div>
          );
        })}
      </div>
      <div className="bg-surface rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-primary mb-4 flex items-center">
          <Icon name="Lightbulb" size={18} className="mr-2 text-secondary sm:w-5 sm:h-5" />
          Key Recommendations
        </h3>
        <div className="space-y-3">
          {results?.recommendations?.map((recommendation, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{recommendation}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-primary mb-2">What This Means for Your Organization</h3>
        <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed mb-4">
          {results?.interpretation}
        </p>
        <div className="flex items-center space-x-2 text-xs sm:text-sm text-secondary">
          <Icon name="TrendingUp" size={14} className="sm:w-4 sm:h-4" />
          <span className="font-medium">Potential ROI Impact: {results?.roiImpact}</span>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <Button
          variant="default"
          fullWidth
          iconName="Download"
          iconPosition="left"
          onClick={onDownloadReport}
          className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
        >
          Download Detailed Report
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="Calendar"
          iconPosition="left"
          onClick={onBookConsultation}
          className="hover:scale-105 hover:shadow-md focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
        >
          Discuss Your Results
        </Button>
      </div>
    </div>
  );
};

export default ResultsCard;